<template>
  <div class="container-fluid py-4">
    <!-- 검색 필터 영역 -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="search-filter-row">
          <div class="search-filter-label">검색어</div>
          <div class="search-filter-content">
            <a-select v-model:value="searchFilter.filter" class="filter-select">
              <a-select-option value="configKey">환경 변수 키</a-select-option>
              <a-select-option value="configValue">환경 변수 값</a-select-option>
            </a-select>
            <a-input
              v-model:value="searchFilter.keyword"
              :placeholder="getSearchPlaceholder"
              class="search-input"
              @pressEnter="handleSearch"
            />
            <div class="button-group">
              <a-button type="primary" class="search-button" @click="handleSearch">검색</a-button>
              <a-button class="reset-button" @click="resetSearch">초기화</a-button>
            </div>
          </div>
        </div>

        <div class="search-filter-row">
          <div class="search-filter-label">민감 정보</div>
          <div class="search-filter-content">
            <a-radio-group 
              v-model:value="searchFilter.isSecret"
              @change="handleSearch"
            >
              <a-radio :value="null">전체</a-radio>
              <a-radio :value="true">민감 정보만</a-radio>
              <a-radio :value="false">일반 정보만</a-radio>
            </a-radio-group>
          </div>
        </div>

        <!-- 환경 변수 타입 필터 추가 -->
        <div class="search-filter-row">
          <div class="search-filter-label">변수 타입</div>
          <div class="search-filter-content">
            <a-select 
              v-model:value="searchFilter.envType" 
              style="width: 200px"
              placeholder="변수 타입 선택"
              @change="handleSearch"
            >
              <a-select-option value="ALL">전체</a-select-option>
              <a-select-option value="MANUFACTURER_ID">제조사 아이디</a-select-option>
              <a-select-option value="KAKAO_TEMPLATE">카카오 알림톡 템플릿</a-select-option>
              <a-select-option value="GENERAL">일반</a-select-option>
            </a-select>
          </div>
        </div>
      </div>
    </div>

    <!-- 테이블 영역 -->
    <div class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <div class="table-info">
            전체 <span class="text-primary">{{ totalCount }}</span>건
          </div>
          
          <div class="d-flex align-items-center gap-3">
            <a-button 
              type="primary" 
              @click="openCreateModal"
            >
              환경 변수 등록
            </a-button>
            <a-button 
              type="primary" 
              @click="openBulkCreateModal"
            >
              일괄 등록
            </a-button>
            <a-select 
              v-model:value="pageSize" 
              class="page-size-select"
              :options="pageSizes"
              @change="onPageSizeChange"
            />
          </div>
        </div>
      </div>

      <div class="card-body table-container">
        <a-table
          :columns="columns"
          :data-source="envConfigs"
          :loading="loading"
          :pagination="false"
        >
          <!-- 환경 변수 키 -->
          <template #configKey="{ text }">
            {{ text }}
          </template>

          <!-- 환경 변수 값 -->
          <template #configValue="{ text, record }">
            <span v-if="record.isSecret">
              ********
              <eye-outlined 
                class="view-icon" 
                @click="showSecretValue(record)" 
              />
            </span>
            <span v-else>{{ text }}</span>
          </template>

          <!-- 설명 -->
          <template #desc="{ text }">
            <a-tooltip :title="text || '-'">
              <span class="ellipsis-text">{{ text || '-' }}</span>
            </a-tooltip>
          </template>

          <!-- 민감 정보 여부 -->
          <template #isSecret="{ text }">
            <a-tag :color="text ? 'red' : 'blue'">
              {{ text ? '민감' : '일반' }}
            </a-tag>
          </template>

          <!-- 변수 타입 -->
          <template #envType="{ text }">
            <a-tag :color="getEnvTypeColor(text)">
              {{ getEnvTypeLabel(text) }}
            </a-tag>
          </template>

          <!-- 생성일 -->
          <template #createAt="{ text }">
            {{ formatDateTime(text) }}
          </template>

          <!-- 수정일 -->
          <template #modifyAt="{ text }">
            {{ formatDateTime(text) }}
          </template>

          <!-- 작업 -->
          <template #action="{ record }">
            <div class="action-buttons">
              <a-button type="link" @click="openEditModal(record)">수정</a-button>
              <a-button 
                type="link" 
                danger 
                @click="confirmDelete(record)"
              >
                삭제
              </a-button>
            </div>
          </template>

          <!-- 번호 -->
          <template #index="{ index }">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </template>
        </a-table>

        <!-- 페이지네이션 -->
        <div class="pagination-wrapper">
          <a-pagination
            v-model:current="currentPage"
            :total="totalCount"
            :page-size="pageSize"
            @change="onPageChange"
          />
        </div>
      </div>
    </div>

    <!-- 모달 컴포넌트들 -->
    <CreateEnvConfigModal
      v-model:visible="createModalVisible"
      @success="handleCreateSuccess"
    />

    <BulkCreateEnvConfigModal
      v-model:visible="bulkCreateModalVisible"
      @success="handleBulkCreateSuccess"
    />

    <EditEnvConfigModal
      v-model:visible="editModalVisible"
      :config="selectedConfig"
      @success="handleEditSuccess"
    />
  </div>
</template>

<script>
import { EyeOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import environmentsApis from './api';
import CreateEnvConfigModal from './components/CreateEnvConfigModal.vue';
import BulkCreateEnvConfigModal from './components/BulkCreateEnvConfigModal.vue';
import EditEnvConfigModal from './components/EditEnvConfigModal.vue';
import {twoBtnDialog} from "@/utils/utils";
import { Tooltip } from 'ant-design-vue';

export default {
  name: 'EnvironmentsList',

  components: {
    EyeOutlined,
    CreateEnvConfigModal,
    BulkCreateEnvConfigModal,
    EditEnvConfigModal,
    Tooltip
  },

  data() {
    return {
      loading: false,
      envConfigs: [],
      totalCount: 0,
      currentPage: 1,
      pageSize: 10,
      searchFilter: {
        filter: 'configKey',
        keyword: '',
        isSecret: null,
        envType: 'ALL'  // 기본값을 전체로 설정
      },
      isAllowed: false, // IP 접근 권한 여부

      // 모달 상태
      createModalVisible: false,
      bulkCreateModalVisible: false,
      editModalVisible: false,
      selectedConfig: null,

      // 테이블 기본 컬럼 정의
      baseColumns: [
        {
          title: '번호',
          key: 'index',
          width: 80,
          align: 'center',
          customRender: ({ index }) => {
            const pageIndex = (this.currentPage - 1) * this.pageSize;
            return pageIndex + index + 1;
          }
        },
        {
          title: '변수 타입',
          dataIndex: 'envType',
          key: 'envType',
          width: 150,
          align: 'center',
          slots: { customRender: 'envType' }
        },
        {
          title: '환경 변수 키',
          dataIndex: 'configKey',
          key: 'configKey',
          slots: { customRender: 'configKey' }
        },
        {
          title: '환경 변수 값',
          dataIndex: 'configValue',
          key: 'configValue',
          slots: { customRender: 'configValue' }
        },
        {
          title: '설명',
          dataIndex: 'desc',
          key: 'desc',
          width: 350,
          slots: { customRender: 'desc' }
        },
        {
          title: '민감 정보',
          dataIndex: 'isSecret',
          key: 'isSecret',
          width: 100,
          align: 'center',
          slots: { customRender: 'isSecret' }
        },
        {
          title: '생성일',
          dataIndex: 'createAt',
          key: 'createAt',
          sorter: true,
          slots: { customRender: 'createAt' }
        },
        {
          title: '수정일',
          dataIndex: 'modifyAt',
          key: 'modifyAt',
          sorter: true,
          slots: { customRender: 'modifyAt' }
        }
      ],
      // 작업 컬럼
      actionColumn: {
        title: '작업',
        key: 'action',
        slots: { customRender: 'action' }
      }
    }
  },

  computed: {
    pageSizes() {
      return [
        { value: 10, label: '10개씩 보기' },
        { value: 20, label: '20개씩 보기' },
        { value: 50, label: '50개씩 보기' }
      ]
    },

    getSearchPlaceholder() {
      return this.searchFilter.filter === 'configKey' 
        ? '환경 변수 키를 입력하세요' 
        : '환경 변수 값을 입력하세요'
    },

    // 동적으로 컬럼을 계산하는 속성
    columns() {
      // 기본 컬럼을 복사
      const columns = [...this.baseColumns];
      
      // isAllowed가 true인 경우에만 작업 컬럼 추가
      if (this.isAllowed) {
        columns.push(this.actionColumn);
      }
      
      return columns;
    }
  },

  created() {
    this.api = environmentsApis(this.$apiInstance, this.$store);
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    fetchData() {
      const params = {
        page: this.currentPage,
        limit: this.pageSize
      };

      // 검색어가 있을 경우에만 파라미터 추가
      if (this.searchFilter.keyword?.trim()) {
        params[this.searchFilter.filter] = this.searchFilter.keyword.trim();
      }

      // 민감 정보 필터가 null이 아닐 때만 파라미터 추가
      if (this.searchFilter.isSecret !== null) {
        params.isSecret = this.searchFilter.isSecret;
      }

      // 환경 변수 타입 필터가 있을 때만 파라미터 추가
      if (this.searchFilter.envType && this.searchFilter.envType !== 'ALL') {
        params.envType = this.searchFilter.envType;
        console.debug('Sending envType:', params.envType); // 디버깅용 로그 추가
      }

      this.loading = true;

      this.api.getEnvConfigs(
        params,
        (response) => {
          console.debug('Response:', response.data); // 디버깅용 로그 추가
          this.envConfigs = response.data.items;
          this.totalCount = response.data.total;
          // IP 접근 권한 여부 저장
          this.isAllowed = response.data.isAllowed || false;
          console.debug('IP 접근 권한:', this.isAllowed); // 디버깅용 로그 추가
          this.loading = false;
        },
        (error) => {
          this.$message.error('데이터 조회 중 오류가 발생했습니다.');
          console.error('Error fetching data:', error);
          this.loading = false;
        }
      );
    },

    handleSearch() {
      if (this.searchFilter.keyword?.trim() === '') {
        this.searchFilter.keyword = '';
      }
      this.currentPage = 1;
      this.fetchData();
    },

    resetSearch() {
      this.searchFilter.filter = 'configKey';
      this.searchFilter.keyword = '';
      this.searchFilter.isSecret = null;
      this.searchFilter.envType = 'ALL';
      this.currentPage = 1;
      this.fetchData();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchData();
    },

    onPageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchData();
    },

    // 모달 관련 메서드들
    openCreateModal() {
      this.createModalVisible = true;
    },

    openBulkCreateModal() {
      this.bulkCreateModalVisible = true;
    },

    openEditModal(record) {
      this.selectedConfig = { ...record };
      this.editModalVisible = true;
    },

    handleCreateSuccess() {
      message.success('환경 변수가 등록되었습니다.');
      this.fetchData();
    },

    handleBulkCreateSuccess() {
      message.success('환경 변수가 일괄 등록되었습니다.');
      this.fetchData();
    },

    handleEditSuccess() {
      message.success('환경 변수가 수정되었습니다.');
      this.fetchData();
    },

    async confirmDelete(record) {
      const isConfirmed = await twoBtnDialog(`환경 변수 삭제`, `"${record.configKey}" 환경 변수를 삭제하시겠습니까?`, '삭제');
      if (!isConfirmed) return;

      await this.api.deleteEnvConfig(
        record.configKey,
        () => {
          message.success('환경 변수가 삭제되었습니다.');
          this.fetchData();
        },
        (error) => {
          message.error('삭제 중 오류가 발생했습니다.');
          console.error('Error deleting config:', error);
        }
      );
    },

    showSecretValue(record) {
      // 민감 정보 보기 기능 구현
      this.api.getEnvConfig(
        record.configKey,
        (response) => {
          message.info(`값: ${response.data.configValue}`);
        },
        (error) => {
          message.error('민감 정보 조회 중 오류가 발생했습니다.');
          console.error('Error fetching secret value:', error);
        }
      );
    },

    getEnvTypeLabel(type) {
      const labels = {
        'MANUFACTURER_ID': '제조사 아이디',
        'KAKAO_TEMPLATE': '카카오 알림톡',
        'GENERAL': '일반'
      };
      return labels[type] || type;
    },

    getEnvTypeColor(type) {
      const colors = {
        'MANUFACTURER_ID': 'purple',
        'KAKAO_TEMPLATE': 'green',
        'GENERAL': 'default'
      };
      return colors[type] || 'default';
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/views/Order/order.scss';

.view-icon {
  margin-left: 8px;
  cursor: pointer;
  color: #1890ff;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

:deep(.ant-table-cell) {
  .ant-typography {
    margin-bottom: 0;
  }
}

.ellipsis-text {
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
